import { useEffect, useRef, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Building2, Save, Upload, Image as ImageIcon, X, FileSignature } from 'lucide-react';
import MarkdownEditor from '../components/MarkdownEditor';

const FIELDS = [
  ['name', 'Company name *', 'text'], ['legal_name', 'Legal name', 'text'],
  ['display_name', 'Brand label (sidebar header)', 'text'],
  ['gstin', 'GSTIN', 'text'], ['pan', 'PAN', 'text'],
  ['city', 'City', 'text'], ['state', 'State', 'text'],
  ['pincode', 'Pincode', 'text'], ['country', 'Country', 'text'],
  ['contact_email', 'Contact email', 'email'], ['contact_phone', 'Contact phone', 'tel'],
];

export default function CompanyProfile() {
  const { user } = useAuth();
  const canEdit = (user?.roles || [user?.role]).includes('admin');
  const [form, setForm] = useState({ country: 'India' });
  const [busy, setBusy] = useState(false);
  const [logoMode, setLogoMode] = useState('url'); // 'url' | 'upload'
  const fileRef = useRef(null);

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get('/company-profile');
      setForm(data);
      if (data?.logo_url?.startsWith('data:')) setLogoMode('upload');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  })(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) return toast.error('Pick an image file (PNG/JPG/SVG)');
    if (f.size > 500_000) return toast.error('Logo too large — keep under 500KB');
    const reader = new FileReader();
    reader.onload = () => { setForm(prev => ({ ...prev, logo_url: reader.result })); };
    reader.readAsDataURL(f);
  };

  const clearLogo = () => setForm(prev => ({ ...prev, logo_url: '' }));

  const save = async () => {
    if (!canEdit) return;
    if (!form.name?.trim()) return toast.error('Company name is required');
    setBusy(true);
    try {
      const payload = { ...form };
      // Strip empty strings — backend uses EmailStr/etc which reject ""
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      await api.put('/company-profile', payload); toast.success('Company profile updated');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="company-profile-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#3D4A8E]/10 flex items-center justify-center"><Building2 className="w-5 h-5 text-[#3D4A8E]" /></div>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Organisation</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 mt-1">Company Profile</h1>
          <p className="text-slate-500 text-sm mt-1">{canEdit ? 'Edit company details, address and logo. Used on offer letters and payslips.' : 'Admin-only. You have read-only access.'}</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-6">
        {/* Logo */}
        <div className="grid md:grid-cols-[auto_1fr] gap-6 items-start">
          <div className="w-32 h-32 rounded-2xl bg-slate-50 border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden" data-testid="cp-logo-preview">
            {form.logo_url
              ? <img src={form.logo_url} alt="Logo" className="w-full h-full object-contain p-2" />
              : <ImageIcon className="w-10 h-10 text-slate-300" />}
          </div>
          <div className="space-y-3">
            <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Company logo</Label>
            <div className="inline-flex gap-1 bg-slate-100 rounded-full p-1" data-testid="cp-logo-mode-switch">
              <button onClick={() => setLogoMode('upload')}
                      className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${logoMode === 'upload' ? 'bg-white text-[#3D4A8E] shadow-sm' : 'text-slate-500'}`}
                      data-testid="cp-mode-upload">Upload file</button>
              <button onClick={() => setLogoMode('url')}
                      className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${logoMode === 'url' ? 'bg-white text-[#3D4A8E] shadow-sm' : 'text-slate-500'}`}
                      data-testid="cp-mode-url">From URL</button>
            </div>
            {logoMode === 'upload' ? (
              <div className="flex items-center gap-3">
                <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="cp-logo-file" disabled={!canEdit} />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={!canEdit}
                        className="rounded-full" data-testid="cp-logo-upload-btn">
                  <Upload className="w-4 h-4 mr-2" /> Choose image
                </Button>
                {form.logo_url && (
                  <Button type="button" variant="ghost" size="sm" onClick={clearLogo} disabled={!canEdit} className="text-red-600" data-testid="cp-logo-clear">
                    <X className="w-4 h-4 mr-1" /> Remove
                  </Button>
                )}
              </div>
            ) : (
              <Input data-testid="cp-logo-url" value={(form.logo_url || '').startsWith('data:') ? '' : (form.logo_url || '')} disabled={!canEdit}
                     onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="rounded-xl max-w-lg" placeholder="https://… (PNG/JPG/SVG)" />
            )}
            <p className="text-xs text-slate-400">PNG / JPG / SVG · max 500KB · square works best</p>
          </div>
        </div>

        <div className="border-t border-slate-100 pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map(([k, label, type]) => (
            <div key={k}>
              <Label className="text-xs font-semibold text-slate-600">{label}</Label>
              <Input data-testid={`cp-${k}`} type={type} value={form[k] || ''} disabled={!canEdit}
                     placeholder={k === 'display_name' ? 'e.g. PROTEINHUNT — shown in sidebar (defaults to company name)' : ''}
                     onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-xl mt-1" />
            </div>
          ))}
          <div className="md:col-span-2">
            <Label className="text-xs font-semibold text-slate-600">Address</Label>
            <Textarea data-testid="cp-address" value={form.address || ''} disabled={!canEdit}
                      onChange={(e) => setForm({ ...form, address: e.target.value })} rows={3} className="rounded-xl mt-1" />
          </div>
        </div>
        {canEdit && (
          <Button data-testid="cp-save" onClick={save} disabled={busy} className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
            <Save className="w-4 h-4 mr-2" /> {busy ? 'Saving…' : 'Save profile'}
          </Button>
        )}
      </div>

      {/* Company stamp / seal (admin only) */}
      <CompanyStampCard canEdit={canEdit} />

      {/* Per-tenant PDF theme (admin & HR can edit) */}
      <PdfThemeCard canEdit={(user?.roles || [user?.role]).some(r => ['admin', 'hr'].includes(r))} />

      {/* Offer-letter templates (R&R + T&C defaults) */}
      <OfferTemplatesCard canEdit={(user?.roles || [user?.role]).some(r => ['admin', 'hr'].includes(r))} />
    </div>
  );
}

function OfferTemplatesCard({ canEdit }) {
  const [tpl, setTpl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get('/offer-templates');
      setTpl(data); setLoaded(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  })(); }, []);

  const save = async () => {
    if (!canEdit || !tpl) return;
    if (!tpl.roles_responsibilities?.trim() || !tpl.terms_conditions?.trim()) {
      toast.error('Both sections are required.'); return;
    }
    setBusy(true);
    try {
      await api.put('/offer-templates', {
        roles_responsibilities: tpl.roles_responsibilities,
        terms_conditions: tpl.terms_conditions,
      });
      toast.success('Offer templates saved — used as default for new offer letters.');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!loaded || !tpl) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5" data-testid="offer-templates-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#3D4A8E]/10 flex items-center justify-center"><FileSignature className="w-5 h-5 text-[#3D4A8E]" /></div>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Offer Letter</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5">Roles &amp; Responsibilities · Terms &amp; Conditions</h2>
          <p className="text-xs text-slate-500 mt-1">These markdown templates pre-fill every new offer letter. HR can still tweak them per candidate.</p>
        </div>
      </div>
      <div>
        <Label className="text-xs font-semibold text-slate-600">Roles &amp; Responsibilities</Label>
        <MarkdownEditor testid="tpl-rr-editor" rows={10}
                        value={tpl.roles_responsibilities}
                        onChange={(v) => setTpl({ ...tpl, roles_responsibilities: v })}
                        className="mt-1.5" />
      </div>
      <div>
        <Label className="text-xs font-semibold text-slate-600">Terms &amp; Conditions</Label>
        <MarkdownEditor testid="tpl-tnc-editor" rows={12}
                        value={tpl.terms_conditions}
                        onChange={(v) => setTpl({ ...tpl, terms_conditions: v })}
                        className="mt-1.5" />
      </div>
      {canEdit ? (
        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-500">
            {tpl.updated_at
              ? <>Last updated {tpl.updated_at.slice(0, 10)}{tpl.updated_by ? ` · by ${tpl.updated_by}` : ''}</>
              : <>Using built-in defaults — save to customise.</>}
          </div>
          <Button data-testid="tpl-save" onClick={save} disabled={busy}
                  className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
            <Save className="w-4 h-4 mr-2" /> {busy ? 'Saving…' : 'Save templates'}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-slate-400">Admin/HR access required to edit templates.</p>
      )}
    </div>
  );
}


function CompanyStampCard({ canEdit }) {
  const [stamp, setStamp] = useState(null); // { content_type, size, filename, uploaded_at, uploaded_by }
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // object URL
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get('/company-profile');
      setStamp(data?.stamp ? { ...data.stamp } : null);
      if (data?.stamp) {
        const resp = await api.get('/company-profile/stamp/preview', { responseType: 'blob' });
        const url = URL.createObjectURL(resp.data);
        setPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
      } else { setPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return null; }); }
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); return () => { setPreview((p) => { if (p) URL.revokeObjectURL(p); return null; }); }; /* eslint-disable-next-line */ }, []);

  const upload = async (f) => {
    if (!f) return;
    const ok = ['image/png', 'image/jpeg', 'image/svg+xml'].includes(f.type) ||
      ['png', 'jpg', 'jpeg', 'svg'].includes(f.name?.toLowerCase().split('.').pop());
    if (!ok) return toast.error('Choose a PNG, JPG, JPEG or SVG.');
    if (f.size > 500_000) return toast.error('Image must be 500 KB or smaller.');
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      await api.post('/company-profile/stamp', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Company stamp saved — will appear on all letter PDFs.');
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const remove = async () => {
    if (!window.confirm('Remove the company stamp? Letter PDFs will be generated without it.')) return;
    setBusy(true);
    try {
      await api.delete('/company-profile/stamp');
      toast.success('Stamp removed.');
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="company-stamp-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
          <ImageIcon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Company stamp / seal</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5">Authorised company seal</h2>
          <p className="text-xs text-slate-500 mt-1">
            Used in the signature block of every employee letter PDF (offer, relieving, experience, termination).
            <b className="text-slate-700"> Transparent PNG recommended.</b> Max 140&nbsp;×&nbsp;140&nbsp;px when rendered.
          </p>
        </div>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row gap-5 items-start">
        <div className="w-40 h-40 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 flex items-center justify-center overflow-hidden flex-shrink-0"
             data-testid="stamp-preview">
          {preview ? (
            <img src={preview} alt="Company stamp" className="max-w-full max-h-full object-contain" />
          ) : (
            <div className="text-xs text-slate-400 text-center px-2">No stamp uploaded yet.</div>
          )}
        </div>
        <div className="flex-1 space-y-2.5">
          {stamp ? (
            <div className="text-xs text-slate-500 space-y-0.5">
              <div><b className="text-slate-700">File:</b> {stamp.filename}</div>
              <div><b className="text-slate-700">Size:</b> {(stamp.size / 1024).toFixed(1)} KB · {stamp.content_type}</div>
              {stamp.uploaded_at && <div><b className="text-slate-700">Uploaded:</b> {stamp.uploaded_at.slice(0, 10)} · {stamp.uploaded_by}</div>}
            </div>
          ) : (
            <p className="text-xs text-slate-400">Once uploaded, the seal appears next to the authorised signature on every employee letter.</p>
          )}
          {canEdit ? (
            <div className="flex flex-wrap gap-2 pt-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,.png,.jpg,.jpeg,.svg"
                     className="hidden" data-testid="stamp-file-input"
                     onChange={(e) => upload(e.target.files?.[0])} />
              <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="stamp-upload-btn"
                      className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white">
                <Upload className="w-4 h-4 mr-1.5" /> {stamp ? 'Replace stamp' : 'Upload stamp'}
              </Button>
              {stamp && (
                <Button variant="outline" onClick={remove} disabled={busy} data-testid="stamp-remove-btn"
                        className="rounded-full border-red-200 text-red-700 hover:bg-red-50">
                  <X className="w-4 h-4 mr-1.5" /> Remove
                </Button>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-400">Admin access required to upload the company stamp.</p>
          )}
        </div>
      </div>
    </div>
  );
}


// ------------------------------------------------------------------
// Per-tenant PDF theme — admin/HR pick colors + logo position for all
// outgoing PDFs (offer letters, payslips, experience certificates).
// ------------------------------------------------------------------
function PdfThemeCard({ canEdit }) {
  const [theme, setTheme] = useState({ primary: '#3D4A8E', accent: '#3D4A8E', logo_position: 'left' });
  const [defaults, setDefaults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get('/tenant/pdf-theme');
      if (data?.theme) setTheme(data.theme);
      if (data?.defaults) setDefaults(data.defaults);
    } catch (e) { /* swallow — non-fatal */ }
    finally { setLoaded(true); }
  })(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put('/tenant/pdf-theme', theme);
      toast.success('Document theme saved. New PDFs will use these colors.');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Failed to save theme');
    } finally { setBusy(false); }
  };

  const reset = () => {
    if (defaults) setTheme({ ...defaults });
  };

  if (!loaded) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-6" data-testid="pdf-theme-card">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Document theme</div>
          <h3 className="font-heading text-xl font-bold text-slate-900 mt-1">PDF colors &amp; logo position</h3>
          <p className="text-sm text-slate-500 mt-1">
            Applied to every PDF generated for your tenant — offer letters, payslips, relieving &amp; experience
            certificates, termination notices. Change here, download a fresh PDF to see the new look.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
        <div>
          <Label className="text-xs font-semibold text-slate-600">Primary color</Label>
          <div className="flex items-center gap-2 mt-1">
            <input type="color" value={theme.primary} disabled={!canEdit}
                   onChange={(e) => setTheme({ ...theme, primary: e.target.value.toUpperCase() })}
                   className="w-12 h-10 rounded-lg border border-slate-200 cursor-pointer"
                   data-testid="pdf-theme-primary-picker" />
            <Input value={theme.primary} disabled={!canEdit} maxLength={7}
                   onChange={(e) => setTheme({ ...theme, primary: e.target.value.toUpperCase() })}
                   className="rounded-xl font-mono" data-testid="pdf-theme-primary-text" />
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Used on the brand bar, NET PAY amount, key labels.</p>
        </div>

        <div>
          <Label className="text-xs font-semibold text-slate-600">Accent color</Label>
          <div className="flex items-center gap-2 mt-1">
            <input type="color" value={theme.accent} disabled={!canEdit}
                   onChange={(e) => setTheme({ ...theme, accent: e.target.value.toUpperCase() })}
                   className="w-12 h-10 rounded-lg border border-slate-200 cursor-pointer"
                   data-testid="pdf-theme-accent-picker" />
            <Input value={theme.accent} disabled={!canEdit} maxLength={7}
                   onChange={(e) => setTheme({ ...theme, accent: e.target.value.toUpperCase() })}
                   className="rounded-xl font-mono" data-testid="pdf-theme-accent-text" />
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Section sub-headings &amp; secondary highlights.</p>
        </div>

        <div>
          <Label className="text-xs font-semibold text-slate-600">Logo position</Label>
          <select value={theme.logo_position} disabled={!canEdit}
                  onChange={(e) => setTheme({ ...theme, logo_position: e.target.value })}
                  className="w-full mt-1 rounded-xl border border-slate-200 h-10 px-3 text-sm bg-white"
                  data-testid="pdf-theme-logo-position">
            <option value="left">Left (default)</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
          <p className="text-[11px] text-slate-400 mt-1">Where your company logo appears in PDF headers.</p>
        </div>
      </div>

      {/* Live preview */}
      <div className="mt-6 border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          Live preview
        </div>
        <div className="p-5 bg-white" data-testid="pdf-theme-preview">
          <div className={`flex items-center gap-3 ${theme.logo_position === 'right' ? 'flex-row-reverse' : theme.logo_position === 'center' ? 'justify-center' : ''}`}>
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500">LOGO</div>
            <div>
              <div className="font-bold text-slate-900">Your Company Name</div>
              <div className="text-xs text-slate-500">Address line · City · Email</div>
            </div>
          </div>
          <div className="h-[3px] my-3" style={{ background: theme.primary }} />
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div><div className="text-[9px] uppercase tracking-wider font-bold text-slate-500">Employee Name</div><div className="text-sm font-semibold text-slate-900 mt-0.5">Jane Doe</div></div>
            <div><div className="text-[9px] uppercase tracking-wider font-bold text-slate-500">Employee ID</div><div className="text-sm font-semibold text-slate-900 mt-0.5">CORP-2026-0042</div></div>
            <div><div className="text-[9px] uppercase tracking-wider font-bold text-slate-500">Designation</div><div className="text-sm font-semibold text-slate-900 mt-0.5">Senior Engineer</div></div>
          </div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] mb-1" style={{ color: theme.accent }}>Sample section heading</div>
          <p className="text-xs text-slate-600 leading-relaxed mb-3">
            This is how a paragraph of body text will appear on the offer letter. Section sub-titles use your accent color.
          </p>
          <div className="h-[3px] my-3" style={{ background: theme.primary }} />
          <div className="flex items-baseline justify-between">
            <div className="font-bold text-base tracking-wider text-slate-900">NET PAY</div>
            <div className="font-bold text-2xl tabular-nums" style={{ color: theme.primary }}>₹1,22,933.33</div>
          </div>
        </div>
      </div>

      {canEdit && (
        <div className="flex items-center gap-2 mt-5">
          <Button onClick={save} disabled={busy} className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]" data-testid="pdf-theme-save">
            <Save className="w-4 h-4 mr-2" /> {busy ? 'Saving…' : 'Save theme'}
          </Button>
          <Button variant="outline" onClick={reset} disabled={busy || !defaults} className="rounded-full" data-testid="pdf-theme-reset">
            Reset to default
          </Button>
        </div>
      )}
      {!canEdit && (
        <p className="text-xs text-slate-400 mt-4">Admin or HR access required to edit document theme.</p>
      )}
    </div>
  );
}
